<template>
  <div class="screen-container" v-if="isLoading">
    <span class="mdi mdi-loading mdi-spin mdi-48px"></span>
  </div>
  <div class="screen-container" v-else>
    <div class="alert-box">
      <!-- 5万プラン上限台数アラート -->
      <ModalAlertDialog
        :isAlertDialogOpen="isAlertDialogOpen"
        :alertData="alertData"
        @alertResult="alertResult"
      />
    </div>
    <!-- 一覧/アーカイブ選択と検索 -->
    <div class="relative-flex-box" ref="roboListHeader">
      <!-- 一覧/アーカイブ表示切替 -->
      <div class="right-align-box">
        <!-- 一覧/アーカイブ選択ドロップダウン -->
        <b-dropdown aria-role="list" position="is-bottom-right">
          <button class="button" type="button" slot="trigger">
            <!-- アーカイブ表示時 -->
            <template v-if="!isArchiveList">
              <b-icon icon="format-list-bulleted-type"></b-icon>
              <span class="-left-8px">ロボの一覧</span>
            </template>
            <!-- 一覧表示時 -->
            <template v-else>
              <b-icon icon="archive"></b-icon>
              <span class="-left-8px">アーカイブ</span>
            </template>
            <b-icon icon="menu-down"></b-icon>
          </button>
          <!-- ロボ一覧表示 -->
          <b-dropdown-item @click="changeArchiveList(false)" aria-role="listitem">
            <div class="media">
              <b-icon class="media-left" icon="format-list-bulleted-type"></b-icon>
              <div class="media-content">
                <h3>ロボ一覧</h3>
                <small>アーカイブされていないロボの一覧</small>
              </div>
            </div>
          </b-dropdown-item>
          <!-- アーカイブ一覧表示 -->
          <b-dropdown-item @click="changeArchiveList(true)" aria-role="listitem">
            <div class="media">
              <b-icon class="media-left" icon="archive"></b-icon>
              <div class="media-content">
                <h3>アーカイブ</h3>
                <small>アーカイブされているロボの一覧</small>
              </div>
            </div>
          </b-dropdown-item>
        </b-dropdown>
      </div>
      <!-- ロボ検索(ロボ名またはタグ) -->
      <div class="right-align-box">
        <input class="input input-w400" placeholder="🔎ロボ名またはタグ名で検索" v-model="searchWords"/>
      </div>
    </div>
    <!-- 雇用ロボ一覧 -->
    <b-tabs class="b-tabs" :multiline="true">
      <!-- タブ -->
      <b-tab-item
        v-for="(tabName, tabIndex) in tabNameList" :key="tabIndex"
        class ="b-tab-item disp-scroll"
        :label="tabName"
        :style="getBodyHeight()"
      >
        <!-- タブ名と台数 -->
        <template #header>
          {{getTabName(tabName)}}
          <b-tag rounded>
            {{getRoboNum(tabName)}}
          </b-tag>
        </template>
        <!-- ロボリスト -->
        <template v-if="getRoboNum(tabName) > 0 && !isLoading">
          <RoboCard
            v-for="robo in getDispRoboList(tabName)" :key="robo.bot_id"
            parent="robo-list"
            :robo="robo"
            :mediaCooperationItemMasterNameList="mediaCooperationItemMasterNameList"
            :PCTRLLOG="CTRLLOG"
          />
        </template>
        <template class="list-nothing-message" v-else>
          表示できるロボがありませんでした。
        </template>
      </b-tab-item>
    </b-tabs>
  </div>
</template>

<script>
import { mapState }        from 'vuex'
import RoboCard            from '@/components2/Robo/modal/RoboCard'
import ModalChatBotDetails from '@/components/ChatBot/organisms/ModalChatBotDetails'
import ModalAlertDialog    from '@/components/organisms/common/ModalAlertDialog'
import controlLogMixin     from '@/mixins/controlLogMixin'
import {
  /** アラートダイアログ用定義 */
  ALERT_DEF,
  /** アラート表示データ作成 */
  createAlertData
} from '@/functions/common/alertFunc'
import {
  isRoboDisplayPermission
} from '@/functions/checkPermission'

export default {
  name: 'RoboEmploymentList',
  componentName: 'RoboEmploymentList',
  components: {
    RoboCard,
    ModalChatBotDetails,
    ModalAlertDialog,
    isRoboDisplayPermission
  },
  mixins: [
    controlLogMixin
  ],
  data () {
    return {
      /**
       * コンポーネント名
       * @type {String}
       */
      COMPONENT_NAME: 'RoboEmploymentList',
      /**
       * ローディング中フラグ
       * @type {Boolean}
       */
      isLoading: false,
      /**
       * 媒体連携項目マスターデータ表の高さ
       * @type {String}
       */
      roboListBodyHeight: '',
      /**
       * タブ名
       * @type {Object}
       */
      TAB_NAME: {
        ALL: '全て',
        MYROBO: 'マイロボ',
        SHARE: '共有',
        FAVORITE: 'お気に入り',
        TEMPLATE: 'テンプレート'
      },
      /**
       * アーカイブ表示フラグ(true：アーカイブ表示、false：一覧表示)
       * @type {Boolean}
       */
      isArchiveList: false,
      /**
       * アラートダイアログ表示フラグ(true：表示、false：非表示)
       * @type {Boolean}
       */
      isAlertDialogOpen: false,
      /**
       * アラートダイアログ表示データ
       * @type {Object}
       */
      alertData: {},
      /**
       * タブごとの表示ロボリストセット
       * @type {Object}
       */
      dispRoboListSet: {},
      /**
       * 検索ワード
       * @type {String}
       */
      searchWords: null,
      /**
       * タブ名リスト
       * @type {Array}
       */
      tabNameList: [],
      /**
       * タグ名リスト
       * @type {Array}
       */
      tagNameList: [],
    }
  },
  computed: {
    ...mapState('auth', ['user', 'roboTagList', 'userChatBotBookMark']),
    ...mapState('chatbot', ['chatbots', 'chatbottags']),
    // 媒体連携関連ストア
    ...mapState('recruitmentMedia', [
      // 媒体連携項目マスター一覧
      'mediaCooperationItemMasterNameList',
    ]),
    // サイドメニュー幅フラグ
    ...mapState("setting",["isMenuMiniSize"]),
  },
  watch: {
    /**
     * サイドメニュー幅フラグ監視
     */
    isMenuMiniSize () {
      // サイドメニュー幅が変わった時に、タブの段数が変わる場合があるので高さを再計算
      // 一度クリアする事で、getBodyHeight実行時に高さを再計算させる
      this.roboListBodyHeight = ''
    },
    chatbots () {
      this.isLoading = true
      // ロボリストを初期化
      this.roboListInit()
    },
    userChatBotBookMark () {
      this.isLoading = true
      // ロボリストを初期化
      this.roboListInit()
    }
  },
  created () {
    // ウィンドウリサイズイベント登録
    window.addEventListener('resize', this.resizeScreenHeight)
    // 媒体連携項目マスター一覧取得
    // ToDo: 項目マスターテーブルを取るだけのAPIに変える
    this.$store.dispatch('recruitmentMedia/getMediaCooperationItemMasterList')
  },
  mounted () {
    this.isLoading = true
    // プランによるロボロック上限台数チェック
    this.isPlan()

    // 表示するタブ名の一覧を作成
    this.tabNameList = []
    // 固定のタブ
    this.tabNameList.push(this.TAB_NAME.ALL)
    this.tabNameList.push(this.TAB_NAME.MYROBO)
    this.tabNameList.push(this.TAB_NAME.SHARE)
    this.tabNameList.push(this.TAB_NAME.FAVORITE)
    if (this.user.companyName === 'テンプレートロボ作成会社') {
      this.tabNameList.push(this.TAB_NAME.TEMPLATE)
    }

    // 会社に登録されているタグ名をリスト化
    let tags = []
    for (let tag of this.chatbottags) {
      tags.push(tag.name)
    }
    // タグ名の重複を削除
    this.tagNameList = Array.from(new Set(tags))
    // タグ名をタブとして追加
    for (let tagName of this.tagNameList) {
      this.tabNameList.push(tagName)
    }
  },
  beforeDestroy () {
    // ウィンドウリサイズイベント破棄
    window.removeEventListener('resize', this.resizeScreenHeight)
  },
  methods: {
    /**
     * リサイズ処理
     */
    resizeScreenHeight () {
      // b-tabsにrefが設定できないので、b-tabsが生成するdivのclassを参照して取得する
      let roboListTab = document.getElementsByClassName("tabs")
      if (roboListTab.length > 0) {
        roboListTab = roboListTab[0]
      } else {
        roboListTab = undefined
      }

      if (this.$refs.roboListHeader && roboListTab) {
        // 要素が描画済みなら高さ調整を行う
        const roboListHeaderHeight = this.$refs.roboListHeader.clientHeight
        const roboListTabHeight = roboListTab.clientHeight
        // 在籍ロボ一覧の高さを調整する
        this.roboListBodyHeight = 'calc(100vh - ' + (roboListHeaderHeight + roboListTabHeight + 10) + 'px)'
      }
    },
    /**
     * 在籍ロボ一覧の高さ取得
     */
    getBodyHeight () {
      if (!this.roboListBodyHeight) {
        this.resizeScreenHeight()
      }
      return {height: this.roboListBodyHeight}
    },
    /**
     * ロボリスト初期化
     */
    roboListInit () {
      // 閲覧権限のあるロボのみを絞り込み
      let authorizedRoboList = this.chatbots.filter(robo => isRoboDisplayPermission(robo, this.user) && robo.is_store === false)

      // タブごとのロボをリスト化
      // 全てタブ
      // 閲覧権限のあるロボを全てリスト化する
      this.dispRoboListSet[this.TAB_NAME.ALL] = authorizedRoboList

      // マイロボタブ
      // 自分で作成したロボのみをリスト化する
      this.dispRoboListSet[this.TAB_NAME.MYROBO] =
        authorizedRoboList.filter(robo => robo.create_by === this.user.userHash)

      // 共有タブ(マイロボは除く)
      // 閲覧権限のあるロボのうち、自分で作成ていないロボをリスト化する
      this.dispRoboListSet[this.TAB_NAME.SHARE] =
        authorizedRoboList.filter(robo => robo.create_by !== this.user.userHash)

      // お気に入りタブ
      this.dispRoboListSet[this.TAB_NAME.FAVORITE] =
        authorizedRoboList.filter(
          authRobo => isRoboDisplayPermission(authRobo, this.user) &&
          this.userChatBotBookMark.filter(bookMarkRobo => bookMarkRobo.chatbot === authRobo.bot_id).length > 0
        )

      // テンプレートロボタブ
      if (this.user.companyName === 'テンプレートロボ作成会社') {
        this.dispRoboListSet[this.TAB_NAME.TEMPLATE] =
          authorizedRoboList.filter(
            authRobo => isRoboDisplayPermission(authRobo, this.user) &&
            authRobo.is_template === true
          )
      }

      // タグタブ
      // 閲覧権限のあるロボをタグごとにリスト化する
      for (const tagName of this.tagNameList) {
        let tagRoboList =
          authorizedRoboList.filter(
            robo => robo.chatbot_tag.filter(tag => tag.name === tagName).length > 0
          )
        this.dispRoboListSet[tagName] = tagRoboList
      }
      this.isLoading = false
    },
    /**
     * プランによるロボ台数上限チェック
     */
    isPlan () {
      // 5万円プランの場合は上限台数をチェックする
      if (this.user.billing === 1) {
        // ロボロックしているロボを取得
        let lockRoboList = this.chatbots.filter(robo => robo.is_confirm === true)
        if (this.user.companyID === '1dad12e572955a7fc7fbb820a0b30a79') {
          // 介護NEXT様用
          if (lockRoboList.length >= 20) {
            this.limitAlert('２０台')
          }
        } else {
          if (lockRoboList.length >= 10) {
            this.limitAlert('１０台')
          }
        }
      }
    },
    /**
     * ロボ台数上限アラートダイアログ表示
     * @param {String} planLimit 上限台数
     */
    limitAlert (planLimit) {
      // アラート表示データ
      const alertData = createAlertData(
        // タイトル
        'ライトプランのお客様へ',
        // 本文
        'ロボ台数の上限に達しました。（上限：' + planLimit + '）\n\n' +
        '新しいロボを使用するには、アーカイブも含めて既存のロボから１台ロック解除するか、スタンダードプランへの移行が必要です。\n',
        // アラートの種類
        ALERT_DEF.MESSAGE_WARNING,
        // ダイアログの種類
        ALERT_DEF.DIALOG_ALERT,
        // 表示元キーワード
        'plan-limit',
        // 表示元分類キーワード
        'plan-limit'
      )

      // アラート表示
      this.alertData         = alertData
      this.isAlertDialogOpen = true
    },
    /**
     * アラートダイアログ操作結果
     * @param  {Object} alertResultData アラートダイアログ操作結果
     */
    alertResult (alertResultData) {
      if (alertResultData) {
        // アラート結果データあり
        if (alertResultData.alertResultValue === ALERT_DEF.RESULT_OK) {
          this.isAlertDialogOpen = false
        }
      }
    },
    /**
     * アーカイブ表示切替
     * @param {Boolean} flg アーカイブ表示切替(true：アーカイブ表示、false：一覧表示)
     */
    changeArchiveList (flg) {
      this.isArchiveList = flg
    },
    /**
     * タブ名取得
     * @param {String} tabName タブ名
     * @return {String} タブ表示名
     */
    getTabName (tabName) {
      let dispTabName = ''
      if (this.tagNameList.filter(tag => tag === tabName).length > 0) {
        // タブ名がタグ名の場合は、先頭に「#」を付ける
        dispTabName = '#' + tabName
      } else {
        // タグ名以外はそのまま返す
        dispTabName = tabName
      }
      return dispTabName
    },
    /**
     * タブに表示されるロボ台数取得
     * @param {String} tabName タブ名
     * @return {Number} ロボ台数
     */
    getRoboNum (tabName) {
      let roboList = this.getDispRoboList(tabName)
      let num = 0
      if (roboList) {
        num = roboList.length
      } else {
        num = 0
      }
      return num
    },
    /**
     * タブに表示されるロボ取得
     * @param {String} tabName タブ名
     * @return {Array} ロボリスト
     */
    getDispRoboList (tabName) {
      let findRoboList = []
      let roboList = this.dispRoboListSet[tabName]
      if (roboList) {
        findRoboList = roboList.filter(robo => robo.is_archive === this.isArchiveList)
        if (this.searchWords) {
          findRoboList = findRoboList.filter(
            robo =>
            // ロボ管理名に部分一致
            robo.name.indexOf(this.searchWords) >= 0 ||
            // ロボ表示名に部分一致
            (robo.display_name && robo.display_name.indexOf(this.searchWords) >= 0) ||
            // 付与されているタグ名に部分一致
            (robo.chatbot_tag && robo.chatbot_tag.find(tag => tag.name.indexOf(this.searchWords) >= 0))
          )
        }
      }
      return findRoboList
    },
  },
}
</script>

<style lang="scss" scoped>
.screen-container {
  width: 100%;
  height: 100vh;
}
.-container {
  width : 100%;
  padding: 4px 16px;
}
.alert-box {
  display: flex;
}
// 一覧/アーカイブ選択と検索
.relative-flex-box {
  width: 100%;
  padding: 8px;
  display: flex;
  justify-content: end;
  position: relative;
  // 要素右寄せ
  .right-align-box {
    padding-left: 16px;
  }
}
// タブ
.b-tabs {
  width: 100%;
  padding: 8px;
  margin-bottom: 0px;
  .b-tab-item {
    padding: 8px;
  }
}
.input-w400 {
  width: 400px;
}
// リスト無しメッセージ
.list-nothing-message {
  position: fixed;
  padding: 24px;
  font-size: 20px;
  font-weight: 600;
}
</style>



// WEBPACK FOOTER //
// src/components2/Robo/screen/RoboEmploymentList.vue